import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, OnDestroy, OnInit, Signal } from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";
import { provideAccountsPayable } from "@tylertech/accounts-payable-module";
import { AggregateManagerService, AggregateService, provideAggregates, TasksComponent } from "@tylertech/aggregates-module";
import { ChartManagerService } from "@tylertech/charting-module";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { CommonDialogsService, IntentActionDialogData } from "@tylertech/common-dialogs-module";
import { FeatureFlagHelperService, FileService, isHttpResponseError, NavMenuService, SecurityService, ToastService, UserProfileService } from "@tylertech/core-module";
import { DialogService, ForgeBannerModule, ForgeButtonModule, ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeMenuModule, ForgeSkeletonModule, ForgeSkipLinkModule, ForgeToolbarModule, ForgeTooltipModule, ForgeViewModule, ForgeViewSwitcherModule } from "@tylertech/forge-angular";
import { provideGeneralLedger } from "@tylertech/general-ledger-module";
import { ShellService } from "@tylertech/incode-module";
import { InfoCardDialogService } from "@tylertech/info-card-module";
import { IntentService } from "@tylertech/intents-module";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { providePackets } from "@tylertech/packets-module";
import { EmployeeLookupComponent, providePersonnel } from "@tylertech/personnel-module";
import { PurchaseOrderApprovalActionHandler, PurchasingDialogsService, RequisitionApprovalActionHandler } from "@tylertech/purchasing-module";
import { provideReporting, ReportDeliveryService } from "@tylertech/reporting-module";
import { ExecutionResult, IAggregate, ICardData, IChartCriteria, IDocument, IDocumentCriteria, IInfoCardData, IIntent, ISearchCriteria, PerformActionParams } from "@tylertech/tyler-api-types";
import { CatchUpBillingActionHandler, LeakNotificationNotSentActionHandler, PendingPaymentPlanActionHandler, provideUtilityBilling, TylerSmartMeterActionHandler, UtilityServiceRequestActionHandler } from "@tylertech/utility-billing-module";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ProcessesComponent } from "../processes/processes.component";
import { DashboardChartComponent } from "./dashboardChart.component";
import { DashboardMetadataComponent } from "./dashboardMetadata.component";
import { EmailTestCardComponent } from "./emailTestCard.component";
import { UnsupportedBrowserComponent } from "./unsupported-browser.component";
import { UserLookupCardComponent } from "./userLookup.component";
import { WidgetBarComponent } from "./widgetBar.component";

const fifteenMinutes = 15 * 60 * 1000; // 900000;

@Component({
    selector: "dashboard",
    imports: [
        BusyIndicatorComponent,
        DashboardChartComponent,
        DashboardMetadataComponent,
        EmailTestCardComponent,
        EmployeeLookupComponent,
        FlexLayoutModule,
        FlexModule,
        ForgeBannerModule,
        ForgeButtonModule,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeMenuModule,
        ForgeSkeletonModule,
        ForgeSkipLinkModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        ForgeViewModule,
        ForgeViewSwitcherModule,
        ProcessesComponent,
        TasksComponent,
        UserLookupCardComponent,
        WidgetBarComponent
    ],
    providers: [
        provideAccountsPayable(),
        provideGeneralLedger(),
        providePackets(),
        providePersonnel(),
        provideReporting(),
        provideUtilityBilling(),
        provideAggregates(
            // Purchasing
            PurchaseOrderApprovalActionHandler,
            RequisitionApprovalActionHandler,
            // UtilityBilling
            CatchUpBillingActionHandler,
            LeakNotificationNotSentActionHandler,
            PendingPaymentPlanActionHandler,
            TylerSmartMeterActionHandler,
            UtilityServiceRequestActionHandler),
    ],
    templateUrl: "./dashboard.tmpl.html",
    styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent
    implements OnDestroy, OnInit
{
    private readonly log = getLoggerFor("DashboardComponent");

    private readonly $securityService = inject(SecurityService);
    private readonly $userProfileService = inject(UserProfileService);
    private readonly $navMenuService = inject(NavMenuService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $shellService = inject(ShellService);
    private readonly $chartManagerService = inject(ChartManagerService);
    private readonly $aggregateManagerService = inject(AggregateManagerService);
    private readonly dialogService = inject(DialogService);

    private readonly destructor$ = new Subject<void>();
    readonly title = "Dashboard";
    readonly page: number = 1;

    classAnimation: string = "rubberBand";
    purchasingLicense: boolean = true;
    isProcessing: boolean;
    fetched: boolean;
    timeElapsed: boolean;
    chartsLoaded: boolean;
    leftCharts: Array<IChartCriteria>;
    rightCharts: Array<IChartCriteria>;
    hiddenCharts: Array<IChartCriteria>;
    chartAggregates: IAggregate[];

    private refreshTimeoutId: ReturnType<typeof setTimeout>;
    readonly devFlag: Signal<boolean>;

    constructor()
    {
        const featureFlagService = inject(FeatureFlagHelperService);
        const activatedRoute = inject(ActivatedRoute);
        const $fileService = inject(FileService);
        const $toastService = inject(ToastService);
        const $aggregateService = inject(AggregateService);
        const infoCardDialogService = inject(InfoCardDialogService);
        const purchasingDialogsService = inject(PurchasingDialogsService);
        const commonDialogsService = inject(CommonDialogsService);
        const $intentService = inject(IntentService);
        const $reportDeliveryService = inject(ReportDeliveryService);

        // FIXME - we should be registering things like this somewhere else.
        $intentService.registerFrontEndAction(
            "smartcard", "comment", "comment", (data?: { readonly Label: string, readonly NextIntent?: IIntent; }) =>
        {
            return commonDialogsService.showComment(data?.Label, data?.NextIntent);
        });

        $intentService.registerFrontEndAction<ISearchCriteria<IInfoCardData>, void>(
            "smartcard", "launchInfoCardSearchDialog", "infoCardSearchDialog", data =>
        {
            return infoCardDialogService.showSearchDialog(data || {});
        });

        // TODO - this should be registered by the PurchasingModule, not here....
        // REVIEW - Does anything even use this intent?
        $intentService.registerFrontEndAction(
            "infocard", "launchBidQuoteDialog", "infoCardBidQuoteDialog", (data: Partial<Record<"id", string>>) =>
        {
            return purchasingDialogsService.showBidQuotes(null, false, true, data?.id);
        });

        $intentService.registerFrontEndAction(
            "smartcard", "intentactiondialog", "web", (data: IntentActionDialogData) =>
        {
            return commonDialogsService.showIntentAction(data ?? {});
        });

        $intentService.registerFrontEndAction(
            "smartcard", "action", "express", (data: Record<"CardData", ICardData>) =>
        {
            const cardData: ICardData = data.CardData;
            const crit = {
                CardProviderId: cardData.CardProviderId,
                CardAction: data["CardAction"],
                Card: cardData,
            } as PerformActionParams;
            return $aggregateService.PerformCardAction(crit)
                .then(
                    (result: ExecutionResult<ICardData>) => result,
                    (err: HttpErrorResponse) =>
                    {
                        if (err && err.error && err.error.Message)
                        {
                            $toastService.showToast(err.error.Message);
                        }
                        else
                        {
                            $toastService.showToast("Failed to perform card action");
                        }
                    });
        });

        $intentService.registerFrontEndAction(
            "smartcard", "print", "express", (data: Record<"DocCrit", IDocumentCriteria>) =>
        {
            const criteria: IDocumentCriteria = data.DocCrit;

            return $reportDeliveryService.GenerateAndView(criteria)
                .then(
                    (document: IDocument) =>
                    {
                        $fileService.openDocumentLink(document.Content, document.MimeType, document.Filename);
                    },
                    (err: unknown) =>
                    {
                        if (isHttpResponseError(err))
                        {
                            $toastService.showToast(err.error.Message);
                        }
                        else
                        {
                            console.error(err);
                        }
                    });
        });

        activatedRoute.paramMap.pipe(takeUntil(this.destructor$))
            .subscribe((params: ParamMap) =>
            {
                if (params.has("module"))
                {
                    // TODO(DO-4549) - We never read this data here. So who are we "communicating" with? And why are we doing it through session storage?
                    window.sessionStorage.removeItem("current_module");
                    window.sessionStorage.removeItem("module_display");

                    this.$navMenuService.setDisabled(true);
                }
            });

        this.$chartManagerService.leftChartsObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((charts: Array<IChartCriteria>) =>
            {
                this.leftCharts = charts;
            });

        this.$chartManagerService.rightChartsObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((charts: Array<IChartCriteria>) =>
            {
                this.rightCharts = charts;
            });

        this.$chartManagerService.hiddenChartsObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((charts: Array<IChartCriteria>) =>
            {
                this.hiddenCharts = charts;
            });

        this.$chartManagerService.chartsLoadedObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                this.chartsLoaded = true;
            });

        this.devFlag = featureFlagService.DevFlag;
    }

    // TODO - why is this hard-coded here?
    private get isHrAnalyst(): boolean { return this.$securityService.hasPersona("HR Analyst"); }
    // TODO - why is this hard-coded here?
    private get isHrDirector(): boolean { return this.$securityService.hasPersona("HR Director"); }
    // TODO - why is this hard-coded here?
    private get isPyAnalyst(): boolean { return this.$securityService.hasPersona("Payroll Analyst"); }
    // TODO - why is this hard-coded here?
    private get isPySupervisor(): boolean { return this.$securityService.hasPersona("Payroll Supervisor"); }

    get embedded(): boolean { return this.$shellService.isEmbedded(); }
    get aggregatesLoaded(): boolean { return this.$aggregateManagerService.AggregatesLoaded; }

    // TODO - why is this hard-coded here?
    get showEmployeeLookup(): boolean
    {
        return this.isHrDirector
            || this.isHrAnalyst
            || this.isPyAnalyst
            || this.isPySupervisor;
    }

    ngOnDestroy(): void
    {
        if (this.refreshTimeoutId !== undefined)
            clearTimeout(this.refreshTimeoutId);
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.refresh()
            .finally(() =>
            {
                this.fetched = true;
            });

        this.checkBrowser();
        window.opener = null;
    }

    private checkBrowser(): void
    {
        const userAgent = window.navigator.userAgent;
        if (userAgent.indexOf("MSIE ") > 0 || userAgent.indexOf("Trident/") > 0)
        {
            this.launchIEWarning();
        }
    }

    private launchIEWarning(): void
    {
        this.dialogService.open(UnsupportedBrowserComponent);
    }

    @LogInvocation(LogLevel.DEBUG)
    refresh(): Promise<void>
    {
        this.timeElapsed = false;
        if (this.refreshTimeoutId !== undefined)
            clearTimeout(this.refreshTimeoutId);

        return Promise.all(
            [
                this.$aggregateManagerService.RefetchAggregates(),
                this.$chartManagerService.updateCharts()
            ])
            .then(() =>
            {
                this.refreshTimeoutId = setTimeout(() => this.timeElapsed = true, fifteenMinutes);
            });
    }

    displayChartOrdering(): void
    {
        const originalLeftCharts = [...this.leftCharts];
        const originalRightCharts = [...this.rightCharts];
        const originalHiddenCharts = [...this.hiddenCharts];

        const groups = [
            this.leftCharts,
            this.rightCharts,
            this.hiddenCharts
        ];
        this.$commonDialogsService.showDragAndDropDialog(groups, "Title", "Application", "Dashboard Configuration", "Smart Charts", ["Left", "Right", "Hidden"])
            .then(
                r =>
                {
                    if (r)
                        this.saveChartSettings(r);
                    else
                    {
                        this.leftCharts = originalLeftCharts;
                        this.rightCharts = originalRightCharts;
                        this.hiddenCharts = originalHiddenCharts;
                    }
                },
                () =>
                {
                    window.location.reload();
                }
            );
    }

    private saveChartSettings(groups: IChartCriteria[][]): void
    {
        this.$userProfileService.SetPositions(groups)
            .finally(() =>
            {
                this.isProcessing = false;
            });
    }

    trackByChartId(_index: number, chart: IChartCriteria): string
    {
        return chart.ChartId;
    }
}
